/**
 * Minimized view for in-flight monitoring
 * Compact: dose dial + alert icon. Toggle to expand full dashboard.
 */

import React from 'react';
import { View, Text, StyleSheet, Pressable, Platform } from 'react-native';
import { CircularExposureDial } from './CircularExposureDial';
import { ICRP_ANNUAL_LIMIT_MSV } from '../utils/constants';

const ICRP_LIMIT_MICRO_SV = ICRP_ANNUAL_LIMIT_MSV * 1_000_000;
const ACCENT_CYAN = '#00b8d9';

interface MinimizedDashboardViewProps {
  annualCumulativeMicroSv: number;
  isAlert: boolean;
  currentDoseRateMicroSvPerHour: number | null;
  onExpand: () => void;
  fallback?: boolean;
  dark?: boolean;
}

const CARD_BG_DARK = '#1e293b';
const CARD_BORDER_DARK = '#334155';

export function MinimizedDashboardView({
  annualCumulativeMicroSv,
  isAlert,
  currentDoseRateMicroSvPerHour,
  onExpand,
  fallback = false,
  dark = false,
}: MinimizedDashboardViewProps) {
  return (
    <Pressable
      style={[
        styles.container,
        isAlert && styles.containerAlert,
        dark && { backgroundColor: CARD_BG_DARK, borderColor: CARD_BORDER_DARK },
        dark && isAlert && { backgroundColor: '#7f1d1d', borderColor: '#ef4444' },
      ]}
      onPress={onExpand}
      accessibilityRole="button"
      accessibilityLabel="Expand dashboard. Tap to view full screen"
    >
      <View style={styles.dialWrapper}>
        <CircularExposureDial
          annualCumulativeMicroSv={annualCumulativeMicroSv}
          limitMicroSv={ICRP_LIMIT_MICRO_SV}
          size={80}
          fallback={fallback}
          dark={dark}
        />
      </View>
      <View style={styles.rightBlock}>
        {isAlert && (
          <View style={styles.alertBadge}>
            <Text style={styles.alertIcon}>⚠</Text>
            <Text style={styles.alertText}>Alert</Text>
          </View>
        )}
        {currentDoseRateMicroSvPerHour != null &&
          currentDoseRateMicroSvPerHour > 0 && (
            <Text style={styles.rateText}>
              {(currentDoseRateMicroSvPerHour).toFixed(2)} µSv/h
            </Text>
          )}
        <Text style={[styles.expandHint, dark && { color: '#94a3b8' }]}>Tap to expand</Text>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    backgroundColor: '#ffffff',
    ...(Platform.OS !== 'web' && {
      shadowColor: '#cbd5e1',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.08,
      shadowRadius: 6,
      elevation: 2,
    }),
  },
  containerAlert: {
    borderColor: '#ef4444',
    backgroundColor: '#fef2f2',
  },
  dialWrapper: {
    marginRight: 16,
  },
  rightBlock: {
    flex: 1,
  },
  alertBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fee2e2',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginBottom: 4,
  },
  alertIcon: {
    fontSize: 14,
    marginRight: 4,
  },
  alertText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#b91c1c',
  },
  rateText: {
    fontSize: 14,
    fontWeight: '600',
    color: ACCENT_CYAN,
    marginBottom: 2,
  },
  expandHint: {
    fontSize: 11,
    color: '#94a3b8',
  },
});
